import { useState } from 'react';
import { format } from 'date-fns';
import Image from 'next/image';

import CommentResponseForm from './CommentResponseForm';
import { BodyRegular, BodySmall } from '@common/body';

import reply from '@public/img/guides/reply@3x.png';

export const COMMENTS_MAX_INDENTATION_LEVEL = 2;

type Props = {
  commentId: number;
  content: string;
  date: string;
  indentationLevel: number;
  name: string;
  postId: number;
  absoluteCanonicalURL: string;
};

const marginLeftIndentations: { [key: number]: string } = {
  0: 'ml-0',
  1: 'ml-[25px]',
  2: 'ml-[50px]',
  3: 'ml-[75px]',
  4: 'ml-[100px]',
  5: 'ml-[125px]',
  6: 'ml-[150px]',
} as const;

const Comment = ({
  commentId,
  content,
  date,
  indentationLevel,
  name,
  postId,
  absoluteCanonicalURL,
}: Props): JSX.Element => {
  const [showReplyForm, setShowReplyForm] = useState(false);

  const toggleShowReplyForm = () => {
    setShowReplyForm(!showReplyForm);
  };

  // A perculiar bug whereby Safari does not recognise a date as valid unless it has the 'T' in the middle
  // e.g. "2020-02-03 12:55:43" is not a valid date. We must insert the T, so it becomes "2020-02-03T12:55:43"
  const commentDate = new Date(date.replace(/ /g, 'T'));

  const marginLeft = Object.prototype.hasOwnProperty.call(
    marginLeftIndentations,
    indentationLevel,
  )
    ? marginLeftIndentations[indentationLevel]
    : 'ml-[175px]';

  return (
    <>
      <div
        className={`rounded bg-white md:mt-[20px] md:px-[25px] md:py-[20px] ${marginLeft} mt-[5px] p-[10px] text-blue`}
      >
        <div className="mb-[10px] flex items-center justify-between">
          <BodyRegular>
            <strong>{name}</strong>
          </BodyRegular>
          <div className="text-blue-600">
            <BodySmall>{format(commentDate, 'd MMMM, yyyy')}</BodySmall>
          </div>
        </div>
        <div dangerouslySetInnerHTML={{ __html: content }} />
        {indentationLevel < COMMENTS_MAX_INDENTATION_LEVEL && (
          <>
            <hr className="my-[15px] border-t-[2px] border-blue-50" />
            <button
              type="button"
              onClick={toggleShowReplyForm}
              className="flex w-full items-center justify-between"
            >
              <Image src={reply} width={20} height={13} alt="Reply" />
              <div className="hover:text-blue-400">
                <BodySmall>Reply</BodySmall>
              </div>
            </button>
          </>
        )}
      </div>
      {showReplyForm && (
        <CommentResponseForm
          postId={postId}
          commentId={commentId}
          absoluteCanonicalURL={absoluteCanonicalURL}
        />
      )}
    </>
  );
};

export default Comment;
